/**
 * @file Redux mappers for composeMetaDueDate container
 */

import { MapDispatchToProps, MapStateToProps } from 'react-redux';

import AppState from '../../../models/state';
import {
  saveDraftDueDateRequest,
  saveDraftDueDateRequestNew,
  setDraftDueDateRequest,
} from '../../../store/actions/draftsDateDue';

import {
  addDraftRemindersRequest,
  removeDraftRemindersRequest,
  saveDraftRemindersRequest,
  saveDraftRemindersRequestNew,
  setDraftRemindersRequest,
} from '../../../store/actions/draftsReminders';

import { DispatchProps, OwnProps, StateProps } from './types';

/**
 * Map Redux action dispatch to component props
 *
 * @param dispatch          Redux action dispatcher
 * @param ownProps          Passed props
 * @param ownProps.nodeType The id of the draft
 * @returns                 Mapped state
 */
export const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (
  dispatch,
  { nodeType },
) => ({
  /**
   * Dispatches request to remove due date draft
   */
  requestDateDueRemove: () => {
    dispatch(setDraftDueDateRequest(nodeType, null));
  },
  /**
   * Dispatches request to save due date draft
   */
  requestDateDueSave: () => {
    dispatch(saveDraftDueDateRequest(nodeType));
  },
  /**
   * Dispatches request to save due date draft
   *
   * @param date Date to be saved as due date
   * @param mode Mode of the date picker (date or time)
   */
  requestDateDueSaveNew: (date, mode) => {
    dispatch(saveDraftDueDateRequestNew(nodeType, date, mode));
  },
  /**
   * Dispatches request to set due date draft
   *
   * @param date Date to be set as due date
   */
  requestDateDueSet: date => {
    dispatch(setDraftDueDateRequest(nodeType, date));
  },
  /**
   * Dispatches request to add reminder draft
   *
   * @param reminder Reminder to be added
   */
  requestRemindersAdd: reminder => {
    dispatch(addDraftRemindersRequest(nodeType, reminder));
  },
  /**
   * Dispatches request to remove reminder draft
   *
   * @param position Position of the reminder that will be removed
   */
  requestRemindersRemove: position => {
    dispatch(removeDraftRemindersRequest(nodeType, position));
  },
  /**
   * Dispatches request to save reminder draft
   */
  requestRemindersSave: () => {
    dispatch(saveDraftRemindersRequest(nodeType));
  },
  /**
   * Dispatches request to save reminder draft
   *
   * @param reminderItems List of reminders
   */
  requestRemindersSaveNew: reminderItems => {
    dispatch(saveDraftRemindersRequestNew(nodeType, reminderItems));
  },
  /**
   * Dispatches request to set reminder draft
   *
   * @param position Position of the reminder that will be set
   * @param reminder Reminder to be set
   */
  requestRemindersSet: (position, reminder) => {
    dispatch(setDraftRemindersRequest(nodeType, position, reminder));
  },
});

/**
 * Map Redux state to container props
 *
 * @param appState          Current app state
 * @param appState.drafts   Drafts in Redux store
 * @param ownProps          Props passed directly
 * @param ownProps.nodeType Draft ID
 * @returns                 Mapped state
 */
export const mapStateToProps: MapStateToProps<
  StateProps,
  OwnProps,
  AppState
> = ({ drafts }, { nodeType }) => ({
  dataDateDueCurrent: drafts.get(nodeType)?.dateDueCurrent ?? null,
  dataDateDueSaved: drafts.get(nodeType)?.dateDueSaved ?? null,
  dataRemindersCurrent: drafts.get(nodeType)?.remindersCurrent ?? [],
  dataRemindersSaved: drafts.get(nodeType)?.remindersSaved ?? [],
});
